import {
  AlertCircle,
  ArrowLeft,
  CalendarClock,
  ExternalLink,
  History,
  ListOrdered,
  School,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';

import { cn } from '@/nucleo/utilitarios';
import { STATUS_TONE, type StatusTone } from '@/nucleo/statusBadgeTokens';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Separator } from '@/ui/base/separator';
import { CopyableText } from '@/ui/CopyableText';
import { ErrorState } from '@/ui/ErrorState';
import { HistoryTimeline, type HistoryTimelineEntry } from '@/ui/HistoryTimeline';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';

import {
  apresentacaoDoStatus,
  CAMINHO_DO_PORTAL_DO_CREDENCIADO,
  formatarCnpj,
  formatarDataHora,
  formatarPlaca,
  formatarTelefone,
  ROTULO_DO_AUTOR,
  ROTULO_DO_CAMPO,
  ROTULO_DO_TIPO_DE_ENTIDADE,
  rotuloDaModalidade,
  type DetalheDoVeiculo,
  type TransicaoDoVeiculo,
} from './AcompanhamentoContrato';
import { RespostaPreCredenciamento } from '../pre-credenciamento/RespostaPreCredenciamento';
import { FormularioDeCorrecao } from './AcompanhamentoCorrecao';
import { CHAVES_DO_ACOMPANHAMENTO, useVeiculoInscrito } from './AcompanhamentoDados';
import { BotaoSair, CartaoParaEntrar } from './AcompanhamentoSessao';
import { useSessaoDoInscrito } from './identidade/api';

/**
 * O andamento de UM veículo inscrito, pelo protocolo —
 * `/credenciados/acompanhamento/:protocolo` (F06, RN-CP-30).
 *
 * Exige a sessão do inscrito; protocolo de outra conta responde como
 * inexistente (a API não diz que ele existe). Mostra status, próximo passo e
 * prazo, os dados do veículo, a correção pedida pela triagem com o formulário
 * de resposta (só os campos pedidos) e a linha do tempo das transições. Quem foi
 * contemplado (PRE_CREDENCIADO) recebe o caminho do Portal do Credenciado — que
 * é outra conta (D-10, D-19): a sessão do inscrito não abre o portal.
 */

const SUPERFICIE_DO_TOM: Record<StatusTone, string> = {
  success: 'border-success/40 bg-success/5',
  warning: 'border-warning/40 bg-warning/5',
  destructive: 'border-destructive/40 bg-destructive/5',
  info: 'border-info/40 bg-info/5',
  neutral: 'border-border bg-muted/40',
};

/** Mais recente primeiro: é o que a pessoa veio ver. */
function entradasDaLinhaDoTempo(transicoes: readonly TransicaoDoVeiculo[]): HistoryTimelineEntry[] {
  return [...transicoes].reverse().map((t, indice) => {
    const para = apresentacaoDoStatus(t.statusPara);
    return {
      id: `${t.quando}-${indice}`,
      icon: para.icone,
      tone: STATUS_TONE[para.tom],
      titulo: t.statusDe ? para.rotulo : 'Inscrição enviada',
      quando: formatarDataHora(t.quando),
      por: ROTULO_DO_AUTOR[t.autor],
      descricao: t.mensagem,
      de: t.statusDe ? apresentacaoDoStatus(t.statusDe).rotulo : undefined,
      para: t.statusDe ? para.rotulo : undefined,
    };
  });
}

export function AcompanhamentoDetalhePage() {
  const { protocolo } = useParams<{ protocolo: string }>();
  const sessao = useSessaoDoInscrito();
  const consulta = useVeiculoInscrito(protocolo, Boolean(sessao.data));

  useEffect(() => {
    document.title = 'Andamento da inscrição — Sistema TEG';
  }, []);

  if (sessao.isPending || (sessao.data && consulta.isPending)) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <Spinner rotulo="Carregando o andamento da inscrição…" />
      </section>
    );
  }

  if (sessao.isError) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <ErrorState
          variant="load-failed"
          title="Não foi possível conferir sua sessão"
          description="Confira sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void sessao.refetch() }}
        />
      </section>
    );
  }

  if (!sessao.data || consulta.error?.situacao === 401) {
    return <CartaoParaEntrar sessaoTerminou={consulta.error?.situacao === 401} />;
  }

  if (consulta.error?.situacao === 404) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <ErrorState
          variant="resource-not-found"
          title="Não encontramos esta inscrição"
          description="Confira o protocolo. Aqui aparecem só as inscrições feitas com a sua conta."
          primaryAction={{ label: 'Ver minhas inscrições', to: '/credenciados/acompanhamento' }}
        />
      </section>
    );
  }

  if (consulta.isError || !consulta.data) {
    return (
      <section className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6">
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar esta inscrição"
          description="Confira sua conexão e tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
          secondaryAction={{ label: 'Ver minhas inscrições', to: '/credenciados/acompanhamento' }}
        />
      </section>
    );
  }

  return <Detalhe veiculo={consulta.data} />;
}

function Detalhe({ veiculo }: { veiculo: DetalheDoVeiculo }) {
  const apresentacao = apresentacaoDoStatus(veiculo.status);
  const contemplado = veiculo.proximoPasso.codigo === 'ACESSAR_PORTAL';
  const cliente = useQueryClient();
  // Confirmar ou desistir (F09) muda o status e a linha do tempo: relê os dois.
  const aoMudar = () =>
    void cliente.invalidateQueries({ queryKey: CHAVES_DO_ACOMPANHAMENTO.todas });

  return (
    <section
      className="mx-auto w-full max-w-3xl space-y-4 px-4 pt-6 pb-12 sm:px-6"
      aria-labelledby="detalhe-titulo"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/credenciados/acompanhamento">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            Minhas inscrições
          </Link>
        </Button>
        <BotaoSair />
      </div>

      <header className="space-y-1">
        <h1 id="detalhe-titulo" className="text-xl font-extrabold sm:text-2xl">
          Veículo {formatarPlaca(veiculo.placa)}
        </h1>
        <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm">
          Protocolo:
          <CopyableText
            value={veiculo.protocolo}
            label={`Copiar o protocolo ${veiculo.protocolo}`}
            className="text-foreground font-medium"
          />
        </p>
      </header>

      {/* Status, próximo passo e prazo */}
      <Card className={cn('border', SUPERFICIE_DO_TOM[apresentacao.tom])}>
        <CardContent className="space-y-3 py-5">
          <div className="flex items-start gap-3">
            <div className={cn('rounded-full p-2', STATUS_TONE[apresentacao.tom])}>
              <apresentacao.icone className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 space-y-1.5">
              <StatusBadge status={apresentacao.variante} label={apresentacao.rotulo} />
              <h2 className="text-sm font-semibold">Próximo passo</h2>
              <p className="text-sm leading-relaxed">{veiculo.proximoPasso.texto}</p>
              {veiculo.prazo && (
                <p className="flex items-center gap-1 text-sm font-medium">
                  <CalendarClock className="text-warning h-4 w-4" aria-hidden />
                  Prazo: {formatarDataHora(veiculo.prazo)}
                </p>
              )}
              {veiculo.listaEspera && (
                <p className="flex items-center gap-1 text-sm font-medium">
                  <ListOrdered className="h-4 w-4" aria-hidden />
                  Posição na lista de espera: {veiculo.listaEspera.posicao}ª
                </p>
              )}
            </div>
          </div>
          {contemplado && (
            <>
              <Separator />
              <p className="text-muted-foreground text-sm">
                O Portal do Credenciado usa outra conta, diferente desta de acompanhamento: o link
                para criar a senha chega no e-mail desta inscrição.
              </p>
              <Button asChild className="h-11 w-full sm:h-10 sm:w-auto">
                <Link to={CAMINHO_DO_PORTAL_DO_CREDENCIADO}>
                  <ExternalLink className="h-4 w-4" aria-hidden />
                  Ir para o Portal do Credenciado
                </Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Resposta ao pré-credenciamento, dentro do prazo (F09, RN-CP-61) */}
      {veiculo.status === 'PRE_CREDENCIADO' && (
        <RespostaPreCredenciamento protocolo={veiculo.protocolo} aoMudar={aoMudar} />
      )}

      {/* O que a triagem pediu */}
      {veiculo.correcao && (
        <Card className="border-warning/40 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertCircle className="text-warning h-4 w-4" aria-hidden />O que o DTP pediu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {veiculo.pendencias.map((p) => (
                <li key={p.id} className="border-border bg-card rounded-lg border p-3 text-sm">
                  <p className="text-muted-foreground text-xs font-medium">
                    {ROTULO_DO_CAMPO[p.campo]}
                  </p>
                  <p className="whitespace-pre-wrap">{p.descricao}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      {veiculo.correcao && <FormularioDeCorrecao key={veiculo.versao} veiculo={veiculo} />}

      {/* Dados do veículo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Dados da inscrição</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground text-xs">Entidade</dt>
              <dd className="font-medium wrap-break-word">
                {veiculo.entidade.nome}{' '}
                <span className="text-muted-foreground font-normal">
                  ({ROTULO_DO_TIPO_DE_ENTIDADE[veiculo.entidade.tipo]} ·{' '}
                  <span className="font-mono">{veiculo.entidade.documentoMascarado}</span>)
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Período de inscrição</dt>
              <dd className="font-medium">{veiculo.ciclo.nome}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Modalidade</dt>
              <dd className="font-medium">{rotuloDaModalidade(veiculo.modalidade)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Diretoria regional</dt>
              <dd className="font-medium">{veiculo.dre?.nome ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">E-mail</dt>
              <dd className="font-medium break-all">{veiculo.contatos.email ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground text-xs">Telefone</dt>
              <dd className="font-medium">{formatarTelefone(veiculo.contatos.telefone)}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground text-xs">Cooperativa indicada</dt>
              <dd className="font-medium">
                {veiculo.cooperativa
                  ? `${veiculo.cooperativa.nome} · ${formatarCnpj(veiculo.cooperativa.cnpj)}`
                  : 'Nenhuma'}
              </dd>
            </div>
          </dl>
          <div className="space-y-2">
            <h3 className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium">
              <School className="h-3.5 w-3.5" aria-hidden />
              Escolas em ordem de preferência
            </h3>
            {veiculo.escolas.length === 0 ? (
              <p className="text-sm">—</p>
            ) : (
              <ol className="space-y-1 text-sm">
                {veiculo.escolas.map((e) => (
                  <li key={e.codigoEol}>
                    <span className="text-muted-foreground">{e.prioridade}ª</span> {e.nome}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Linha do tempo */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" aria-hidden />
            Histórico
          </CardTitle>
        </CardHeader>
        <CardContent>
          {veiculo.linhaDoTempo.length === 0 ? (
            <p className="text-muted-foreground text-sm">Ainda não há movimentação registrada.</p>
          ) : (
            <HistoryTimeline entradas={entradasDaLinhaDoTempo(veiculo.linhaDoTempo)} />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

export default AcompanhamentoDetalhePage;
